import { motion } from "framer-motion";
import { MessageSquareText, Rocket, LayoutDashboard, LifeBuoy, ArrowRight } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { scrollToHash } from "./SmoothScroll";

const STEPS = [
  {
    n: "01",
    icon: MessageSquareText,
    title: "Raccontaci la tua situazione",
    desc: "Condividi con noi le informazioni necessarie sulla tua attività e sulla tua Partita IVA.",
  },
  {
    n: "02",
    icon: Rocket,
    title: "Accedi al tuo spazio Human Factor",
    desc: "Trovi in un unico ambiente le informazioni, i documenti, le attività e le scadenze che riguardano la tua posizione.",
  },
  {
    n: "03",
    icon: LayoutDashboard,
    title: "Segui ciò che conta",
    desc: "Controlla gli adempimenti e consulta le informazioni essenziali senza dover ricostruire ogni volta che cosa è stato fatto e che cosa manca.",
  },
  {
    n: "04",
    icon: LifeBuoy,
    title: "Chiedi aiuto quando ne hai bisogno",
    desc: "Se hai un dubbio o devi prendere una decisione, puoi rivolgerti direttamente al professionista che ti segue.",
  },
];

export default function ComeFunziona() {
  return (
    <section id="come-funziona" data-testid="section-come-funziona" className="relative py-24 lg:py-32">
      <div className="pointer-events-none absolute left-[-10%] top-[30%] h-[420px] w-[420px] rounded-full bg-[#1B6EF3]/10 blur-[130px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div className="flex flex-col gap-8 lg:sticky lg:top-28 lg:self-start">
          <SectionHead
            eyebrow="Come funziona"
            title={
              <>
                Semplice fin dall'inizio: <span className="text-gradient">dalla burocrazia</span> a un percorso chiaro
              </>
            }
          />
          <Reveal delay={0.2}>
            <a
              href="#offerta"
              data-testid="come-funziona-cta"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("#offerta");
              }}
              className="group inline-flex w-fit items-center gap-2.5 rounded-full bg-gradient-to-r from-[#1B6EF3] to-[#19D6E8] px-7 py-3.5 font-display text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-[1.03] hover:shadow-cyan-400/40"
            >
              Scopri Human Factor
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="relative hidden aspect-square w-full overflow-hidden rounded-3xl border border-white/10 lg:block">
              <img
                src="https://images.unsplash.com/photo-1780733065500-4284d227f58c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Un commercialista Human Factor stringe la mano a un giovane professionista"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/90 via-transparent to-transparent" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(25,214,232,0.15),transparent_55%)]" />
            </div>
          </Reveal>
        </div>

        <div className="relative">
          <div className="absolute bottom-8 left-[27px] top-8 hidden w-px bg-gradient-to-b from-[#1B6EF3] via-[#19D6E8] to-transparent sm:block" />
          <div className="flex flex-col gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                data-testid={`come-funziona-step-${s.n}`}
                className="glow-card group relative flex gap-5 rounded-3xl p-6 sm:p-7 sm:pl-8"
              >
                <div className="relative flex flex-col items-center">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1B6EF3] to-[#19D6E8] font-display text-lg font-extrabold text-white shadow-lg shadow-blue-500/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                    {s.n}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <s.icon className="h-5 w-5 text-cyan-300" />
                  <h3 className="font-display text-xl font-bold tracking-tight">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
